<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, ref } from 'vue'
import { useMutation, useQuery } from '@vue/apollo-composable'
import { GlbStatus, Mutation, PermissionOrganization } from '@/generated/graphql'
import gql from 'graphql-tag'
import DataTable from '@/components/dataTable/DataTable.vue'
import DataTableCreateButton from '@/components/dataTable/DataTableCreateButton.vue'
import ButtonDelete from '@/components/button/ButtonDelete.vue'
import AsyncDeleteDialog from '@/components/dialogs/AsyncDeleteDialog.vue'
import UploadPicker, { PickedUpload } from '@/components/upload/UploadPicker.vue'
import useContentTypes from '@/composables/useContentTypes'
import useAuthorization from '@/composables/useAuthorization'
import { ComponentExposed } from 'vue-component-type-helpers'
import { v4 } from 'uuid'

type BopUploadsDataQuery = {
  product: {
    bop: {
      uploads: Array<{
        id: string
        glbStatus: string
        inUse: boolean
        upload: {
          id: string
          name: string
          contentType: string
        }
      }>
    }
  }
}

const props = defineProps<{
  bopId: string
  isWritable: boolean
}>()

const { t } = useI18n()
const { hasOrgAuthz } = useAuthorization()
const { contentTypesStep } = useContentTypes()

const fetchQuery = useQuery<BopUploadsDataQuery, { bopId: string }>(
  gql`
    query BopUploadsData($bopId: ID!) {
      product {
        bop(id: $bopId) {
          uploads {
            id
            glbStatus
            inUse
            upload {
              id
              name
              contentType
            }
          }
        }
      }
    }
  `,
  () => ({ bopId: props.bopId }),
)

const uploads = computed(() => fetchQuery.result.value?.product.bop.uploads || [])

const createMutation = useMutation<
  Mutation,
  { command: { id?: string; bopId: string; uploadId: string } }
>(gql`
  mutation BopUploadsCreate($command: CreateBOPUpload!) {
    product {
      createBOPUpload(command: $command) {
        id
      }
    }
  }
`)

const uploadsPickerOpen = ref(false)

const onPick = (picked: PickedUpload[]) => {
  Promise.all(
    picked.map((p) =>
      createMutation.mutate({
        command: { id: v4(), bopId: props.bopId, uploadId: p.id },
      }),
    ),
  ).then(() => fetchQuery.refetch())
}

const deleteMutation = useMutation<Mutation, { id: string }>(gql`
  mutation BopUploadsDelete($id: ID!) {
    product {
      deleteBOPUpload(id: $id)
    }
  }
`)
const deleteDialog = ref<ComponentExposed<typeof AsyncDeleteDialog>>()
const doDelete = async (item: BopUploadsDataQuery['product']['bop']['uploads'][0]) => {
  await deleteDialog.value?.open(item.upload.name, () =>
    deleteMutation.mutate({ id: item.id }).then(() => fetchQuery.refetch()),
  )
}

defineExpose({ refresh: () => fetchQuery.refetch() })

const headers = computed(() => [
  { key: 'upload.name', title: t('entity.upload.field.name') },
  { key: 'active', width: 90, sortable: false, align: 'end' as const },
  { key: 'actions', width: 60, sortable: false, align: 'end' as const },
])
</script>

<template>
  <data-table
    :loading="fetchQuery.loading"
    :headers="headers"
    :items="uploads"
    :items-per-page="-1"
  >
    <template
      v-if="isWritable && hasOrgAuthz(PermissionOrganization.OrgProductWrite)"
      #header.actions
    >
      <data-table-create-button
        :title="t('component.bopUploads.addButton')"
        @click="uploadsPickerOpen = true"
      />
    </template>
    <template #item.active="{ item }">
      <v-chip v-if="item.inUse" color="success" size="small" density="comfortable">
        {{ t('component.bopUploads.active') }}
      </v-chip>
      <v-chip
        v-else-if="item.glbStatus === GlbStatus.Failed"
        color="error"
        size="small"
        density="comfortable"
      >
        {{ t('component.bopUploads.conversionFailed') }}
      </v-chip>
      <v-chip
        v-else-if="item.glbStatus === GlbStatus.Converting"
        color="info"
        size="small"
        density="comfortable"
      >
        {{ t('component.bopUploads.converting') }}
      </v-chip>
      <v-chip
        v-else-if="item.glbStatus === GlbStatus.Queued"
        color="grey"
        size="small"
        density="comfortable"
      >
        {{ t('component.bopUploads.queued') }}
      </v-chip>
    </template>
    <template #item.actions="{ item }">
      <button-delete
        v-if="isWritable && hasOrgAuthz(PermissionOrganization.OrgProductDelete)"
        @click="doDelete(item)"
      />
    </template>
  </data-table>

  <upload-picker
    v-model="uploadsPickerOpen"
    :mime-types="contentTypesStep"
    @pick-multiple="onPick"
  />

  <async-delete-dialog ref="deleteDialog" />
</template>

<style scoped lang="scss"></style>
